import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import {
  HiArrowLeft,
  HiTruck,
  HiLocationMarker,
  HiClipboardCopy,
  HiPhone,
  HiUser,
  HiSearch,
} from "react-icons/hi";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { getLoggedInUser } from "../utils/authUser";

const API_BASE = "https://app.caaficare.so/api";

const DEFAULT_PAYMENT_METHODS = [
  { id: 1, name: "EVC Plus", merchant_number: "0619002269", ussd_prefix: "*789*", image: "/evcPlus.jpg" },
  { id: 2, name: "Edahab", merchant_number: "622222154", ussd_prefix: "*712*", image: "/edahab.jpg" },
  { id: 3, name: "Premier Wallet", merchant_number: "737721", ussd_prefix: "*880*", image: "/zaad-logo1.png" },
];

const DriverBooking = () => {
  const navigate = useNavigate();
  const [districts, setDistricts] = useState([]);
  const [pickupPoints, setPickupPoints] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState(DEFAULT_PAYMENT_METHODS);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [fromDistrict, setFromDistrict] = useState("");
  const [toDistrict, setToDistrict] = useState("");
  const [pickupPointId, setPickupPointId] = useState("");
  const [pickupPointSearch, setPickupPointSearch] = useState("");
  const [pickupPointOpen, setPickupPointOpen] = useState(false);
  const [fromDistrictSearch, setFromDistrictSearch] = useState("");
  const [fromDistrictOpen, setFromDistrictOpen] = useState(false);
  const [toDistrictSearch, setToDistrictSearch] = useState("");
  const [toDistrictOpen, setToDistrictOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const pickupPointRef = React.useRef(null);
  const fromDistrictRef = React.useRef(null);
  const toDistrictRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickupPointRef.current && !pickupPointRef.current.contains(e.target)) {
        setPickupPointOpen(false);
      }
      if (fromDistrictRef.current && !fromDistrictRef.current.contains(e.target)) {
        setFromDistrictOpen(false);
      }
      if (toDistrictRef.current && !toDistrictRef.current.contains(e.target)) {
        setToDistrictOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredFromDistricts = React.useMemo(() => {
    const list = districts.filter((d) => d.code !== toDistrict);
    if (!fromDistrictSearch.trim()) return list;
    const q = fromDistrictSearch.toLowerCase();
    return list.filter((d) => (d.name || "").toLowerCase().includes(q) || (d.code || "").toLowerCase().includes(q));
  }, [districts, toDistrict, fromDistrictSearch]);

  const filteredToDistricts = React.useMemo(() => {
    const list = districts.filter((d) => d.code !== fromDistrict);
    if (!toDistrictSearch.trim()) return list;
    const q = toDistrictSearch.toLowerCase();
    return list.filter((d) => (d.name || "").toLowerCase().includes(q) || (d.code || "").toLowerCase().includes(q));
  }, [districts, fromDistrict, toDistrictSearch]);

  const filteredPickupPoints = React.useMemo(() => {
    if (!pickupPointSearch.trim()) return pickupPoints;
    const q = pickupPointSearch.toLowerCase();
    return pickupPoints.filter((pp) => {
      const fromD = districts.find((x) => x.code === pp.from);
      const toD = districts.find((x) => x.code === pp.to);
      const fromName = (fromD?.name || pp.from || "").toLowerCase();
      const toName = (toD?.name || pp.to || "").toLowerCase();
      const price = String(pp.price || "");
      const kg = String(pp.number_of_kg || "");
      return fromName.includes(q) || toName.includes(q) || price.includes(q) || kg.includes(q);
    });
  }, [pickupPoints, pickupPointSearch, districts]);
  const [submitting, setSubmitting] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [lastBooking, setLastBooking] = useState(null);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);

  const loadDistricts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/districts?page=1&pageSize=1000`);
      const data = res.data?.data || res.data || [];
      setDistricts(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Districts load error:", e);
      toast.error("Failed to load districts");
      setDistricts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDistricts();
  }, [loadDistricts]);

  useEffect(() => {
    if (fromDistrict && toDistrict && fromDistrict !== toDistrict) {
      const fetchPP = async () => {
        try {
          const res = await axios.get(`${API_BASE}/pickupPoints?page=1&pageSize=500`);
          const all = res.data?.data || res.data || [];
          const list = Array.isArray(all) ? all : [];
          const filtered = list.filter(
            (pp) => pp.from === fromDistrict && pp.to === toDistrict
          );
          setPickupPoints(filtered);
          setPickupPointId("");
        } catch (e) {
          console.error("Pickup points error:", e);
          setPickupPoints([]);
        }
      };
      fetchPP();
    } else {
      setPickupPoints([]);
      setPickupPointId("");
    }
  }, [fromDistrict, toDistrict]);

  const loadCustomerFromStorage = useCallback(() => {
    try {
      const data = getLoggedInUser();
      if (!data) return;
      if (data.full_name || data.fullName || data.name) {
        setCustomerName(data.full_name || data.fullName || data.name);
      }
      if (data.phone || data.tel) {
        setCustomerPhone(data.phone || data.tel || "");
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    loadCustomerFromStorage();
  }, [loadCustomerFromStorage]);

  const getDistrictName = (code) => {
    const d = districts.find((x) => x.code === code);
    return d ? d.name : code;
  };

  const selectedPickupPoint = pickupPoints.find(
    (pp) => String(pp.id) === String(pickupPointId)
  );
  const price = selectedPickupPoint?.price || "0";

  // Step 1: validate and open payment modal (booking not created yet)
  const handleProceedToPayment = (e) => {
    e.preventDefault();
    if (!customerName.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!customerPhone.trim()) {
      toast.error("Please enter your phone number");
      return;
    }
    if (!fromDistrict || !toDistrict) {
      toast.error("Please select from and to district");
      return;
    }
    if (!pickupPointId) {
      toast.error("Please select a pickup point");
      return;
    }
    setShowPayment(true);
  };

  // Step 2: create booking only when user confirms payment
  const handleConfirmPayment = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE}/driver-booking`, {
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim(),
        from_district: fromDistrict,
        to_district: toDistrict,
        pickup_point_id: pickupPointId,
      });

      if (res.data?.success) {
        const data = res.data.data;
        setLastBooking({
          bookingNumber: data.booking_number,
          totalAmount: data.price || price,
        });
        toast.success("Driver booking registered successfully!");
      } else {
        toast.error(res.data?.error || "Failed to register booking");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
          error?.message ||
          "Failed to register booking"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const getUssdCode = (method, amount) => {
    const prefix = method.ussd_prefix || "*712*";
    const merchant = method.merchant_number || "";
    const amt = String(amount || "0").replace(/[$,]/g, "").trim() || "0";
    return `${prefix}${merchant}*${amt}#`;
  };

  const copyUssdCode = (method, amount) => {
    const code = getUssdCode(method, amount);
    if (code && navigator.clipboard) {
      navigator.clipboard.writeText(code);
      toast.success("USSD code with amount copied!");
    }
  };

  useEffect(() => {
    if (showPayment) {
      const fetchPaymentMethods = async () => {
        try {
          const res = await axios.get(`${API_BASE}/payment-methods?active=true`);
          if (res.data?.success && res.data.data?.length > 0) {
            const methods = res.data.data.map((m, i) => ({
              id: m.id ?? i + 1,
              name: m.name,
              merchant_number: m.merchant_number,
              ussd_prefix:
                m.ussd_prefix ||
                (m.name?.toLowerCase().includes("evc")
                  ? "*789*"
                  : m.name?.toLowerCase().includes("edahab")
                  ? "*712*"
                  : "*880*"),
              image:
                m.image ||
                (m.name?.toLowerCase().includes("evc")
                  ? "/evcPlus.jpg"
                  : m.name?.toLowerCase().includes("edahab")
                  ? "/edahab.jpg"
                  : "/zaad-logo1.png"),
            }));
            setPaymentMethods(methods);
            setSelectedPaymentId(methods[0]?.id ?? null);
          } else {
            setPaymentMethods(DEFAULT_PAYMENT_METHODS);
            setSelectedPaymentId(DEFAULT_PAYMENT_METHODS[0]?.id ?? null);
          }
        } catch {
          setPaymentMethods(DEFAULT_PAYMENT_METHODS);
          setSelectedPaymentId(DEFAULT_PAYMENT_METHODS[0]?.id ?? null);
        }
      };
      fetchPaymentMethods();
    }
  }, [showPayment]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-gray-500 font-bold">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        onAppointmentClick={() => navigate("/hospitals")}
        onNurseAppointmentClick={() => navigate("/nurses-list")}
        onVideoConsultingClick={() => navigate("/doctors")}
        onApplyNurseClick={() => {}}
        onApplyDoctorClick={() => {}}
      />

      <div className="pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-amber-600 mb-6 transition-colors"
          >
            <HiArrowLeft className="w-5 h-5" />
            <span className="text-sm font-bold">Back</span>
          </button>

          <div className="bg-white rounded-3xl shadow-xl border-2 border-gray-100 p-8 mb-8 text-center">
            <HiTruck className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h1 className="text-2xl font-black text-gray-900 mb-2">Book Driver</h1>
            <p className="text-gray-500">
              Select your route and pickup point to book a driver for delivery.
            </p>
          </div>

          <form onSubmit={handleProceedToPayment} className="space-y-6">
            <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Your Name *
                  </label>
                  <div className="relative">
                    <HiUser className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="Enter your name"
                      className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-amber-500 focus:ring-4 focus:ring-amber-100 outline-none transition-all"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <div className="relative">
                    <HiPhone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      value={customerPhone}
                      onChange={(e) => setCustomerPhone(e.target.value)}
                      placeholder="Enter phone number"
                      className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-amber-500 focus:ring-4 focus:ring-amber-100 outline-none transition-all"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-6" ref={fromDistrictRef}>
              <label className="block text-sm font-bold text-gray-700 mb-3">
                From District *
              </label>
              <div className="relative">
                <div className="relative">
                  <HiSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={fromDistrictOpen ? fromDistrictSearch : (districts.find((d) => d.code === fromDistrict)?.name || "")}
                    onChange={(e) => {
                      setFromDistrictSearch(e.target.value);
                      setFromDistrictOpen(true);
                    }}
                    onFocus={() => {
                      setFromDistrictOpen(true);
                      setFromDistrictSearch("");
                    }}
                    placeholder="Search from district..."
                    className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-amber-500 focus:ring-4 focus:ring-amber-100 outline-none transition-all"
                  />
                </div>
                {fromDistrictOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 max-h-52 overflow-y-auto bg-white rounded-xl border-2 border-gray-200 shadow-xl z-10">
                    {filteredFromDistricts.length === 0 ? (
                      <div className="px-4 py-6 text-center text-gray-500 text-sm">No districts match your search</div>
                    ) : (
                      filteredFromDistricts.map((d) => (
                        <button
                          key={d.id || d.code}
                          type="button"
                          onClick={() => {
                            setFromDistrict(d.code);
                            setFromDistrictSearch("");
                            setFromDistrictOpen(false);
                          }}
                          className={`w-full text-left px-4 py-3 border-b border-gray-100 last:border-0 hover:bg-amber-50 transition-colors font-semibold text-gray-900 ${
                            fromDistrict === d.code ? "bg-amber-50" : ""
                          }`}
                        >
                          {d.name}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-6" ref={toDistrictRef}>
              <label className="block text-sm font-bold text-gray-700 mb-3">
                To District *
              </label>
              <div className="relative">
                <div className="relative">
                  <HiSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={toDistrictOpen ? toDistrictSearch : (districts.find((d) => d.code === toDistrict)?.name || "")}
                    onChange={(e) => {
                      setToDistrictSearch(e.target.value);
                      setToDistrictOpen(true);
                    }}
                    onFocus={() => {
                      setToDistrictOpen(true);
                      setToDistrictSearch("");
                    }}
                    placeholder="Search to district..."
                    className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-amber-500 focus:ring-4 focus:ring-amber-100 outline-none transition-all"
                  />
                </div>
                {toDistrictOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 max-h-52 overflow-y-auto bg-white rounded-xl border-2 border-gray-200 shadow-xl z-10">
                    {filteredToDistricts.length === 0 ? (
                      <div className="px-4 py-6 text-center text-gray-500 text-sm">No districts match your search</div>
                    ) : (
                      filteredToDistricts.map((d) => (
                        <button
                          key={d.id || d.code}
                          type="button"
                          onClick={() => {
                            setToDistrict(d.code);
                            setToDistrictSearch("");
                            setToDistrictOpen(false);
                          }}
                          className={`w-full text-left px-4 py-3 border-b border-gray-100 last:border-0 hover:bg-amber-50 transition-colors font-semibold text-gray-900 ${
                            toDistrict === d.code ? "bg-amber-50" : ""
                          }`}
                        >
                          {d.name}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {fromDistrict && toDistrict && fromDistrict !== toDistrict && (
              <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-6" ref={pickupPointRef}>
                <label className="block text-sm font-bold text-gray-700 mb-3">
                  Pickup Point * {pickupPoints.length > 0 && `(${pickupPoints.length} available)`}
                </label>
                <div className="relative">
                  <div className="relative">
                    <HiSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={pickupPointOpen ? pickupPointSearch : (selectedPickupPoint ? `${getDistrictName(selectedPickupPoint.from)} → ${getDistrictName(selectedPickupPoint.to)} (${selectedPickupPoint.number_of_kg} kg) - $${selectedPickupPoint.price}` : "")}
                      onChange={(e) => {
                        setPickupPointSearch(e.target.value);
                        setPickupPointOpen(true);
                      }}
                      onFocus={() => {
                        setPickupPointOpen(true);
                        setPickupPointSearch("");
                      }}
                      placeholder="Search pickup point by route, price, or kg..."
                      className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-amber-500 focus:ring-4 focus:ring-amber-100 outline-none transition-all"
                    />
                  </div>
                  {pickupPointOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 max-h-52 overflow-y-auto bg-white rounded-xl border-2 border-gray-200 shadow-xl z-10">
                      {filteredPickupPoints.length === 0 ? (
                        <div className="px-4 py-6 text-center text-gray-500 text-sm">No pickup points match your search</div>
                      ) : (
                        filteredPickupPoints.map((pp) => (
                          <button
                            key={pp.id}
                            type="button"
                            onClick={() => {
                              setPickupPointId(String(pp.id));
                              setPickupPointSearch("");
                              setPickupPointOpen(false);
                            }}
                            className={`w-full text-left px-4 py-3 border-b border-gray-100 last:border-0 hover:bg-amber-50 transition-colors flex items-center justify-between gap-3 ${
                              pickupPointId === String(pp.id) ? "bg-amber-50" : ""
                            }`}
                          >
                            <span className="font-semibold text-gray-900">
                              {getDistrictName(pp.from)} → {getDistrictName(pp.to)}
                            </span>
                            <span className="text-sm text-gray-500">
                              {pp.number_of_kg} kg · {pp.hours_of_delivery}h {pp.minutes_of_delivery}m
                            </span>
                            <span className="font-bold text-amber-600">${pp.price}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {selectedPickupPoint && (
              <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-6 flex items-center justify-between">
                <span className="font-bold text-gray-800">Delivery Fee</span>
                <span className="text-2xl font-black text-amber-600">${price}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={!pickupPointId}
              className="w-full py-4 bg-amber-500 text-white rounded-2xl font-black text-lg shadow-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
            >
              <HiTruck className="w-6 h-6" />
              Proceed to Payment
            </button>
          </form>
        </div>
      </div>

      {/* Payment Modal: first show payment instructions, then on "I've made the payment" we register the booking */}
      {showPayment && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto">
          <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex-shrink-0 bg-gradient-to-r from-amber-500 to-amber-600 px-8 py-6">
              <h2 className="text-2xl font-black text-white">
                {lastBooking ? "Booking Registered Successfully" : "Make Payment"}
              </h2>
              {lastBooking?.bookingNumber && (
                <p className="text-amber-100 mt-1">#{lastBooking.bookingNumber}</p>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-8 min-h-0">
              {lastBooking ? (
                <div className="text-center py-6">
                  <p className="text-gray-600 mb-2">Your driver booking has been registered.</p>
                  <p className="text-lg font-bold text-gray-900">Booking #{lastBooking.bookingNumber}</p>
                  <p className="text-sm text-gray-500 mt-2">You will be contacted for driver assignment.</p>
                </div>
              ) : (
                <>
                  <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6 mb-6 text-center">
                    <p className="text-sm font-bold text-amber-800 mb-1">Amount to Pay</p>
                    <p className="text-3xl font-black text-amber-900">${price || "0.00"}</p>
                  </div>

                  <p className="font-bold text-gray-900 mb-4">Pay via Mobile Money</p>
                  <div className="space-y-4">
                    {paymentMethods.map((method) => {
                      const isSelected = selectedPaymentId === method.id;
                      const imageSrc = method.image?.startsWith("http")
                        ? method.image
                        : method.image || null;
                      return (
                        <div
                          key={method.id}
                          className={`p-4 rounded-2xl border-2 transition-all ${
                            isSelected
                              ? "border-amber-500 ring-2 ring-amber-200 bg-amber-50"
                              : "border-gray-200 bg-white hover:border-amber-200"
                          }`}
                        >
                          <div className="flex items-center gap-3 mb-3">
                            {imageSrc && (
                              <img
                                src={imageSrc}
                                alt={method.name}
                                className="w-12 h-12 rounded-xl object-contain bg-gray-50"
                              />
                            )}
                            <div>
                              <p className="font-bold text-gray-900">{method.name}</p>
                              <p className="text-xs text-gray-500">
                                Pay ${price || "0"} using {method.name}
                              </p>
                            </div>
                          </div>
                          {method.merchant_number && (
                            <div className="mt-3 pt-3 border-t border-gray-200 flex items-center gap-2">
                              <code className="flex-1 font-mono text-sm font-bold text-amber-700 break-all">
                                {getUssdCode(method, price)}
                              </code>
                              <button
                                type="button"
                                onClick={() => copyUssdCode(method, price)}
                                className="p-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 flex-shrink-0"
                                title="Copy USSD code"
                              >
                                <HiClipboardCopy className="w-5 h-5" />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mt-6">
                    <p className="font-bold text-amber-800 mb-2">Payment Instructions</p>
                    <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
                      <li>Copy the USSD code above (includes the amount)</li>
                      <li>Dial the code on your phone or paste in your mobile money app</li>
                      <li>After you have made the payment, tap the button below to register your booking</li>
                    </ul>
                  </div>
                </>
              )}
            </div>
            <div className="flex-shrink-0 px-6 pb-6 pt-4 border-t border-gray-100">
              {lastBooking ? (
                <button
                  type="button"
                  onClick={() => {
                    setShowPayment(false);
                    setLastBooking(null);
                  }}
                  className="w-full py-4 bg-amber-500 text-white rounded-xl font-bold hover:bg-amber-600"
                >
                  Done
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleConfirmPayment}
                  disabled={submitting}
                  className="w-full py-4 bg-amber-500 text-white rounded-xl font-bold hover:bg-amber-600 disabled:opacity-70 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Registering booking...
                    </>
                  ) : (
                    "I've made the payment – Register booking"
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default DriverBooking;
